import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Utensils, FileDown, ShieldCheck } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { resolveLogoUrl } from '../../../lib/resolveLogoUrl';
import { generateTechnicalSheetPdf } from '../../../lib/menusConvivesTechnicalSheetPdfGenerator';
import { generateClientRecapPdf } from '../../../lib/menusConvivesClientRecapPdfGenerator';
import { COLLECTE_STATUS_LABELS, COLLECTE_STATUS_ORDER } from '../collecteStatus';
import { RegimeCoveragePanel, AllergenTerm } from './RegimeCoveragePanel';
import { ClotureProgrammeeCard } from './ClotureProgrammeeCard';

interface EvenementielEvent {
    id: string;
    type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    start_time: string;
    num_people: number;
}

interface MenuStructureCategory { category: string; dish_ids: string[]; }
interface Menu { id: string; name: string; structure: MenuStructureCategory[]; }
// `ingredients` et `dietary_tags` arrivent bien du catalogue (mapMenuItem côté serveur) : ils sont typés ici
// parce que l'analyse de couverture des régimes s'en sert, alors que la page ne les affichait pas.
interface MenuItem { id: string; name: string; category: string; ingredients?: any[]; dietary_tags?: string[]; }

interface Collecte {
    id: string;
    status: string;
    menu_ids: string[];
    dish_quotas: Record<string, number>;
    tables_planned_count: number;
    guest_choice_mode: 'convive' | 'organisateur';
    fixed_dish_ids: Record<string, string>;
    production_locked_at: string | null;
    table_numbers: string[];
    public_token: string;
    guest_instruction: string;
    allow_alt_dish_request?: boolean | number;
    responses_close_date?: string | null;
    prewarning_date?: string | null;
    close_notify_enabled?: boolean | number;
    prewarning_sent_at?: string | null;
    responses_closed_at?: string | null;
}

const eventLabel = (e: EvenementielEvent) => e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');

/** Page "Configuration" du module Menus & Convives — rattacher une/des formule(s) du catalogue à CET
 *  événement, poser les quotas par plat, et le nombre de tables prévues. Ne recrée rien de la création/
 *  édition d'événement (EventForm.tsx). Volontairement séparée du tableau de suivi des convives/invitations
 *  (ConvivesSuiviPage.tsx) — retour d'Emeni : les deux sur un seul écran faisaient "brouillon". */
export function EvenementielConvivesPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [event, setEvent] = React.useState<EvenementielEvent | null>(null);
    const [collecte, setCollecte] = React.useState<Collecte | null>(null);
    const [menus, setMenus] = React.useState<Menu[]>([]);
    const [menuItems, setMenuItems] = React.useState<MenuItem[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [menuIdsDraft, setMenuIdsDraft] = React.useState<string[]>([]);
    const [dishQuotasDraft, setDishQuotasDraft] = React.useState<Record<string, string>>({});
    const [guestChoiceModeDraft, setGuestChoiceModeDraft] = React.useState<'convive' | 'organisateur'>('convive');
    const [fixedDishIdsDraft, setFixedDishIdsDraft] = React.useState<Record<string, string>>({});
    const [savingMenu, setSavingMenu] = React.useState(false);
    const [downloadingSheet, setDownloadingSheet] = React.useState(false);
    const [validating, setValidating] = React.useState(false);
    const [downloadingRecap, setDownloadingRecap] = React.useState(false);
    const [instructionDraft, setInstructionDraft] = React.useState('');
    const [savingInstruction, setSavingInstruction] = React.useState(false);
    // Termes maison de l'établissement (renommages, groupes ajoutés/retirés) : nécessaires pour tester
    // exactement les régimes que le convive pourra cocher, pas une liste théorique.
    const [allergenTerms, setAllergenTerms] = React.useState<AllergenTerm[] | null>(null);
    const [allowAltDraft, setAllowAltDraft] = React.useState(true);
    const [closeDateDraft, setCloseDateDraft] = React.useState('');
    // Cochée par défaut, y compris pour les collectes créées avant l'ajout du réglage (colonne absente).
    const [closeNotifyDraft, setCloseNotifyDraft] = React.useState(true);
    const [prewarnDateDraft, setPrewarnDateDraft] = React.useState('');
    const [savingDates, setSavingDates] = React.useState(false);

    const load = React.useCallback(() => {
        if (!eventId) return;
        setLoading(true);
        Promise.all([
            moduleApi.getEvenementielEvent(eventId),
            moduleApi.getEvenementielCollecte(eventId),
            moduleApi.getMenus().catch(() => []),
            moduleApi.getMenuItems().catch(() => []),
            moduleApi.getAllergenTerms().catch(() => []),
        ]).then(([ev, col, m, mi, at]) => {
            setEvent(ev);
            setCollecte(col);
            setMenuIdsDraft(Array.isArray(col?.menu_ids) ? col.menu_ids : []);
            const quotas = col?.dish_quotas && typeof col.dish_quotas === 'object' ? col.dish_quotas : {};
            setDishQuotasDraft(Object.fromEntries(Object.entries(quotas).map(([k, v]) => [k, String(v)])));
            setGuestChoiceModeDraft(col?.guest_choice_mode === 'organisateur' ? 'organisateur' : 'convive');
            setFixedDishIdsDraft(col?.fixed_dish_ids && typeof col.fixed_dish_ids === 'object' ? col.fixed_dish_ids : {});
            setInstructionDraft(col?.guest_instruction || '');
            // Une collecte créée avant l'ajout du réglage n'a pas la colonne : l'absence vaut "activé",
            // comme côté serveur, sinon la case s'afficherait décochée alors que le convive voit le bouton.
            setAllowAltDraft(col?.allow_alt_dish_request === undefined || col?.allow_alt_dish_request === null ? true : !!col.allow_alt_dish_request);
            setCloseDateDraft(col?.responses_close_date || '');
            setPrewarnDateDraft(col?.prewarning_date || '');
            setCloseNotifyDraft(col?.close_notify_enabled === undefined || col?.close_notify_enabled === null ? true : !!col.close_notify_enabled);
            setMenus(Array.isArray(m) ? m : []);
            setMenuItems(Array.isArray(mi) ? mi : []);
            setAllergenTerms(Array.isArray(at) ? at : []);
        }).catch((e: any) => setError(e?.message || "Erreur de chargement.")).finally(() => setLoading(false));
    }, [eventId]);

    React.useEffect(() => { load(); }, [load]);

    const toggleMenuId = (menuId: string) => setMenuIdsDraft((prev) => prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]);

    // Union dédupliquée des plats des formules cochées (brouillon, pas seulement enregistré — pour voir les
    // plats apparaître/disparaître dès qu'on coche/décoche, avant même d'enregistrer), regroupée par la
    // catégorie propre à chaque plat (pas celle de la formule d'où il vient : un même plat peut être classé
    // différemment selon la formule, et le but est justement de pouvoir piocher dans plusieurs formules).
    const dishesByCategory = React.useMemo(() => {
        const dishIds = new Set<string>();
        menus.filter((m) => menuIdsDraft.includes(m.id)).forEach((m) => m.structure.forEach((s) => s.dish_ids.forEach((id) => dishIds.add(id))));
        const groups: Record<string, MenuItem[]> = {};
        menuItems.filter((i) => dishIds.has(i.id)).forEach((i) => {
            const cat = i.category || 'Autre';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(i);
        });
        return groups;
    }, [menus, menuIdsDraft, menuItems]);

    // Ce que le convive verra RÉELLEMENT : un plat laissé sans quota n'est pas autorisé et n'apparaît pas
    // dans ses choix (même règle que computeAvailableDishes côté serveur). Analyser la couverture des
    // régimes sans ce filtre annoncerait des plats disponibles que personne ne peut choisir.
    const dishesForCoverage = React.useMemo(() => {
        const out: Record<string, MenuItem[]> = {};
        Object.entries(dishesByCategory).forEach(([category, dishes]) => {
            const allowed = dishes.filter((d) => {
                const q = dishQuotasDraft[d.id];
                if (q === '-1') return true;
                return Number(q) > 0;
            });
            if (allowed.length) out[category] = allowed;
        });
        return out;
    }, [dishesByCategory, dishQuotasDraft]);

    // Garde-fous des dates de clôture : une relance postérieure à la clôture n'arriverait jamais, et une
    // clôture après l'événement n'a aucun sens (la cuisine a besoin des choix AVANT le jour J).
    const eventDay = event?.start_time ? String(event.start_time).slice(0, 10) : '';
    const dateError = React.useMemo(() => {
        if (prewarnDateDraft && closeDateDraft && prewarnDateDraft > closeDateDraft) {
            return "L'email d'avertissement doit partir au plus tard le dernier jour pour répondre.";
        }
        if (eventDay && closeDateDraft && closeDateDraft > eventDay) {
            return "Le dernier jour pour répondre doit précéder l'événement.";
        }
        if (prewarnDateDraft && !closeDateDraft) {
            return "Précisez d'abord le dernier jour pour répondre : l'email annonce cette date limite.";
        }
        return '';
    }, [prewarnDateDraft, closeDateDraft, eventDay]);

    const saveDates = async () => {
        if (!eventId || dateError) return;
        setSavingDates(true);
        setError('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, {
                responses_close_date: closeDateDraft || '',
                prewarning_date: prewarnDateDraft || '',
                close_notify_enabled: closeNotifyDraft,
            });
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible d'enregistrer les dates de clôture.");
        } finally {
            setSavingDates(false);
        }
    };

    const setDishQuota = (dishId: string, value: string) => setDishQuotasDraft((prev) => ({ ...prev, [dishId]: value }));

    const saveMenus = async () => {
        if (!eventId) return;
        setSavingMenu(true);
        setError('');
        try {
            // '-1' = illimité explicitement coché (case à cocher) ; une valeur vide/0 = plat NON AUTORISÉ
            // pour cette collecte (absent des choix du convive) — décision d'Emeni (2026-08-22) : "ceux que
            // j'ai laissé vide sont ceux qui ne sont pas autorisés, pas en illimité", inverse le comportement
            // historique où un champ vide voulait dire illimité.
            const dish_quotas: Record<string, number> = {};
            Object.entries(dishQuotasDraft).forEach(([id, v]) => {
                if (v === '-1') { dish_quotas[id] = -1; return; }
                const n = Number(v);
                if (n > 0) dish_quotas[id] = Math.floor(n);
            });
            await moduleApi.updateEvenementielCollecte(eventId, {
                menu_ids: menuIdsDraft,
                dish_quotas,
                guest_choice_mode: guestChoiceModeDraft,
                fixed_dish_ids: guestChoiceModeDraft === 'organisateur' ? fixedDishIdsDraft : {},
                allow_alt_dish_request: allowAltDraft,
            });
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSavingMenu(false);
        }
    };

    // Message d'instruction personnalisable en tête du lien envoyé au convive (ex: date limite de réponse,
    // consignes de tenue...) — demande explicite d'Emeni (2026-08-22).
    const saveInstruction = async () => {
        if (!eventId) return;
        setSavingInstruction(true);
        setError('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, { guest_instruction: instructionDraft });
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement de l'instruction.");
        } finally {
            setSavingInstruction(false);
        }
    };

    // Fiche récap CLIENT (Jour J) — ce que les convives ont choisi, table incluse si déjà déclarée. Demande
    // explicite d'Emeni (2026-08-22).
    const downloadClientRecap = async () => {
        if (!eventId || !event) return;
        setDownloadingRecap(true);
        setError('');
        try {
            const recap: any = await moduleApi.getEvenementielCollecteClientRecap(eventId);
            await generateClientRecapPdf({
                eventLabel: eventLabel(event),
                eventDate: event.start_time,
                guests: Array.isArray(recap?.guests) ? recap.guests.map((g: any) => ({ name: g.name, tableNumber: g.table_number, dishesByCategory: g.dishes_by_category || {} })) : [],
                issuerName: user?.company_name || undefined,
                logoUrl: user?.logo_url ? resolveLogoUrl(user.logo_url) : null,
                brandColor: user?.theme_color || undefined,
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la génération du récap.');
        } finally {
            setDownloadingRecap(false);
        }
    };

    // Fiche technique cuisine (Phase 5) — total par plat choisi + allergies/intolérances déclarées, pour que
    // la cuisine adapte. Demande explicite d'Emeni (2026-08-22).
    const downloadTechnicalSheet = async () => {
        if (!eventId || !event) return;
        setDownloadingSheet(true);
        setError('');
        try {
            const sheet: any = await moduleApi.getEvenementielCollecteTechnicalSheet(eventId);
            await generateTechnicalSheetPdf({
                eventLabel: eventLabel(event),
                eventDate: event.start_time,
                totalResponded: sheet?.total_responded || 0,
                dishes: Array.isArray(sheet?.dishes) ? sheet.dishes : [],
                restrictionsAllergies: Array.isArray(sheet?.restrictions_allergies) ? sheet.restrictions_allergies : [],
                issuerName: user?.company_name || undefined,
                logoUrl: user?.logo_url ? resolveLogoUrl(user.logo_url) : null,
                brandColor: user?.theme_color || undefined,
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la génération de la fiche technique.');
        } finally {
            setDownloadingSheet(false);
        }
    };

    // "Valider" (Phase 5) : verrouille définitivement les réponses des convives (plus aucune modification
    // possible depuis leur lien) — jamais réversible, demande explicite d'Emeni (2026-08-22).
    const handleValidate = async () => {
        if (!eventId) return;
        if (!window.confirm('Valider les réponses ? Les convives ne pourront plus modifier leur choix après cette action.')) return;
        setValidating(true);
        setError('');
        try {
            await moduleApi.validateEvenementielCollecte(eventId);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la validation.');
        } finally {
            setValidating(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!event || !collecte) return <p className="text-sm text-content-muted">Événement introuvable.</p>;

    const statusIdx = COLLECTE_STATUS_ORDER.indexOf(collecte.status);
    const reachedResponsesTerminees = statusIdx >= COLLECTE_STATUS_ORDER.indexOf('reponses_terminees');
    const isValidated = !!collecte.production_locked_at;

    return (
        <div className="space-y-6 max-w-2xl">
            <div>
                <button onClick={() => navigate(`/menus-convives/${event.id}`)} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content mb-3">
                    <ArrowLeft size={14} /> Retour au suivi
                </button>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className="text-xl font-bold text-content tracking-tight">{eventLabel(event)}</h1>
                        <p className="text-content-muted text-sm mt-0.5">
                            {new Date(event.start_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                    <span className="text-xs font-black uppercase px-3 py-1.5 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20">
                        {COLLECTE_STATUS_LABELS[collecte.status] || collecte.status}
                    </span>
                </div>
            </div>

            {reachedResponsesTerminees && (
                <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-3">
                    <h3 className="text-sm font-bold text-content flex items-center gap-2"><ShieldCheck size={15} /> Validation & production</h3>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={downloadTechnicalSheet} disabled={downloadingSheet}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-border text-content hover:bg-surface transition-colors disabled:opacity-50">
                            {downloadingSheet ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Télécharger la fiche technique cuisine
                        </button>
                        <button type="button" onClick={downloadClientRecap} disabled={downloadingRecap}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-border text-content hover:bg-surface transition-colors disabled:opacity-50">
                            {downloadingRecap ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Récap convives (client)
                        </button>
                        {isValidated ? (
                            <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-green-500/10 text-green-500 border border-green-500/20">
                                <ShieldCheck size={13} /> Validé — réponses verrouillées
                            </span>
                        ) : (
                            <button type="button" onClick={handleValidate} disabled={validating}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-brand-600 text-brand-fg hover:bg-brand-700 transition-colors disabled:opacity-50">
                                {validating ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Valider
                            </button>
                        )}
                    </div>
                    {!isValidated && <p className="text-[11px] text-content-muted">Une fois validé, les convives ne pourront plus modifier leur choix de menu ni leurs restrictions.</p>}
                </div>
            )}

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-2">
                <h3 className="text-sm font-bold text-content">Instruction pour le convive</h3>
                <p className="text-[11px] text-content-muted">Affichée tout en haut du lien envoyé par email (ex: date limite de réponse, consignes de tenue...). Laisse vide pour n'afficher que le message par défaut.</p>
                <textarea value={instructionDraft} onChange={(e) => setInstructionDraft(e.target.value)} rows={2} maxLength={2000}
                    placeholder="Ex: Merci de répondre avant le 1er septembre."
                    className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                {/* Le réglage "bloquer les incompatibilités non résolues" a été retiré : une restriction ou une
                    allergie non résolue bloque désormais toujours l'envoi, le rendre optionnel n'avait plus de
                    sens et laissait croire qu'on pouvait laisser passer un convive non servi correctement.
                    Seules les traces n'ont jamais bloqué. Décision d'Emeni (2026-08-23). */}
                <div className="flex justify-end">
                    <button onClick={saveInstruction} disabled={savingInstruction || instructionDraft === collecte.guest_instruction}
                        className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                        {savingInstruction ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>

            <ClotureProgrammeeCard
                closeDate={closeDateDraft} prewarnDate={prewarnDateDraft} closeNotify={closeNotifyDraft}
                onCloseDateChange={setCloseDateDraft} onPrewarnDateChange={setPrewarnDateDraft}
                onCloseNotifyChange={setCloseNotifyDraft}
                onSave={saveDates} saving={savingDates} error={dateError}
                alreadyClosed={!!collecte.responses_closed_at}
                prewarningSentAt={collecte.prewarning_sent_at}
                savedCloseDate={collecte.responses_close_date || ''}
                savedPrewarnDate={collecte.prewarning_date || ''}
                savedCloseNotify={collecte.close_notify_enabled === undefined || collecte.close_notify_enabled === null ? true : !!collecte.close_notify_enabled} />

            <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-content flex items-center gap-2"><Utensils size={15} /> Menu</h3>
                {menus.length === 0 ? (
                    <p className="text-xs text-content-muted">Aucune formule dans ton catalogue — crée-en une depuis le module "Menu & Plats".</p>
                ) : (
                    <>
                        <p className="text-[11px] text-content-muted">Plusieurs formules peuvent être rattachées en même temps (ex: des groupes de convives différents).</p>
                        <div className="flex flex-wrap gap-2">
                            {menus.map((m) => (
                                <button key={m.id} type="button" onClick={() => toggleMenuId(m.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${menuIdsDraft.includes(m.id) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                    {m.name}
                                </button>
                            ))}
                        </div>
                        {Object.keys(dishesByCategory).length > 0 && (
                            <div className="space-y-1.5 pt-1">
                                <h4 className="text-xs font-bold text-content">Qui choisit le repas ?</h4>
                                <div className="flex flex-wrap gap-2">
                                    <button type="button" onClick={() => setGuestChoiceModeDraft('convive')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${guestChoiceModeDraft === 'convive' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        Chaque convive choisit
                                    </button>
                                    <button type="button" onClick={() => setGuestChoiceModeDraft('organisateur')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${guestChoiceModeDraft === 'organisateur' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        Menu fixé par vous
                                    </button>
                                </div>
                                <p className="text-[11px] text-content-muted">
                                    {guestChoiceModeDraft === 'convive'
                                        ? 'Chaque convive choisit son plat sur son lien.'
                                        : "Un seul plat par catégorie s'applique à tous les convives — leur lien ne demande alors que les restrictions et allergies."}
                                </p>
                            </div>
                        )}
                        {Object.keys(dishesByCategory).length > 0 && guestChoiceModeDraft === 'convive' && (
                            <div className="space-y-3 pt-1">
                                <p className="text-[11px] text-content-muted">Précise combien de convives prennent chaque plat — laisse vide pour ne PAS autoriser ce plat, ou coche "Illimité" pour n'en limiter aucunement le nombre.</p>
                                {Object.entries(dishesByCategory).map(([category, dishes]) => {
                                    const total = dishes.reduce((sum, d) => sum + (dishQuotasDraft[d.id] === '-1' ? 0 : (Number(dishQuotasDraft[d.id]) || 0)), 0);
                                    return (
                                        <div key={category} className="bg-surface border border-border rounded-xl p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-xs font-bold text-content">{category}</h4>
                                                {event.num_people > 0 && <span className="text-[10px] text-content-muted">{total} / {event.num_people} personnes couvertes</span>}
                                            </div>
                                            <div className="space-y-1.5">
                                                {dishes.map((d) => {
                                                    const isUnlimited = dishQuotasDraft[d.id] === '-1';
                                                    return (
                                                        <div key={d.id} className="flex items-center justify-between gap-2">
                                                            <span className="text-xs text-content">{d.name}</span>
                                                            <div className="flex items-center gap-2 shrink-0">
                                                                <label className="flex items-center gap-1 text-[10px] text-content-muted cursor-pointer">
                                                                    <input type="checkbox" checked={isUnlimited} onChange={(e) => setDishQuota(d.id, e.target.checked ? '-1' : '')}
                                                                        className="w-3.5 h-3.5 rounded border-border accent-brand-600" />
                                                                    Illimité
                                                                </label>
                                                                <input type="number" min={0} disabled={isUnlimited}
                                                                    value={isUnlimited ? '' : (dishQuotasDraft[d.id] || '')} onChange={(e) => setDishQuota(d.id, e.target.value)}
                                                                    placeholder="Non autorisé"
                                                                    className="w-28 bg-surface-soft border border-border rounded-md px-2 py-1 text-xs text-content outline-none focus:border-brand-500 text-right disabled:opacity-40" />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* Placé sous les quotas, car il dépend d'eux : un plat laissé "non autorisé"
                                    ne compte pas comme une issue pour le convive. */}
                                <RegimeCoveragePanel dishesByCategory={dishesForCoverage} allergenTerms={allergenTerms} loading={allergenTerms === null} />
                                <label className="flex items-start gap-2 text-[11px] text-content cursor-pointer bg-surface border border-border rounded-xl p-3">
                                    <input type="checkbox" checked={allowAltDraft} onChange={(e) => setAllowAltDraft(e.target.checked)}
                                        className="w-3.5 h-3.5 rounded border-border accent-brand-600 mt-0.5 shrink-0" />
                                    <span>
                                        <span className="font-bold block">Autoriser un convive à demander une alternative</span>
                                        <span className="block text-[10px] text-content-muted">
                                            Quand aucun plat d'une catégorie ne lui convient, le convive peut vous le signaler au lieu
                                            de seulement renoncer — sa fiche l'indique et vous pouvez lui proposer un plat pris ailleurs
                                            dans votre catalogue. Décochez si vous ne souhaitez pas recevoir ce type de demande.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}
                        {Object.keys(dishesByCategory).length > 0 && guestChoiceModeDraft === 'organisateur' && (
                            <div className="space-y-3 pt-1">
                                <p className="text-[11px] text-content-muted">Choisis le plat de chaque catégorie qui sera servi à tous les convives.</p>
                                {Object.entries(dishesByCategory).map(([category, dishes]) => (
                                    <div key={category} className="bg-surface border border-border rounded-xl p-3 flex items-center justify-between gap-2">
                                        <h4 className="text-xs font-bold text-content">{category}</h4>
                                        <select value={fixedDishIdsDraft[category] || ''} onChange={(e) => setFixedDishIdsDraft((prev) => ({ ...prev, [category]: e.target.value }))}
                                            className="bg-surface-soft border border-border rounded-md px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500">
                                            <option value="">— Choisir —</option>
                                            {dishes.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex justify-end">
                            <button onClick={saveMenus} disabled={savingMenu}
                                className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                                {savingMenu ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </>
                )}
            </div>

        </div>
    );
}
